import React, { useState } from 'react';
import { Search, Zap, TrendingUp, Bell, Flame, User, Trophy, Swords, MessageCircle, Crown, Newspaper, ArrowLeftRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AuthModal from './AuthModal';

const OhTechYesHeader = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { user, isAuthenticated } = useAuth();

  return (
    <header className="bg-[#0a0a0b] border-b-2 border-[#00ff88] sticky top-0 z-50">
      {/* Top Bar with Logo */}
      <div className="max-w-[1400px] mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <Zap className="w-10 h-10 text-[#00ff88] neon-glow" />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                <span className="text-gradient">Oh Tech Yes</span>
              </h1>
              <p className="text-[10px] text-[#00d9ff] uppercase tracking-widest font-bold">The Hype is Real</p>
            </div>
          </Link>

          {/* Search Bar */}
          <div className="flex-1 max-w-xl mx-8">
            <div className="relative group">
              <input
                type="text"
                placeholder="Search the hype..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-5 py-3 bg-[#1a1a1f] border-2 border-[#00ff88]/20 rounded-xl text-white placeholder-[#6b6b73] focus:outline-none focus:border-[#00ff88] focus:shadow-[0_0_20px_rgba(0,255,136,0.3)] transition-all"
                style={{fontFamily: 'Inter, sans-serif'}}
              />
              <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-[#00ff88] w-5 h-5" />
            </div>
          </div>

          {/* Auth & CTA Buttons */}
          <div className="flex gap-3">
            {isAuthenticated ? (
              <Link
                to="/profile"
                className="flex items-center gap-3 px-4 py-2 bg-zinc-800 border border-green-500/30 rounded-lg hover:border-green-500 transition"
              >
                <img
                  src={user.avatar_url}
                  alt={user.username}
                  className="w-8 h-8 rounded-full border-2 border-green-500"
                />
                <div className="text-left">
                  <p className="text-white font-bold text-sm">{user.username}</p>
                  <p className="text-xs text-green-400">Level {user.level}</p>
                </div>
              </Link>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-[#00ff88] to-[#00d9ff] text-[#0a0a0b] rounded-lg font-bold text-sm uppercase tracking-wide hover:scale-105 transition-transform neon-glow"
              >
                <User className="w-4 h-4" />
                Login
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="bg-[#13131a] border-t border-[#00ff88]/10">
        <div className="max-w-[1400px] mx-auto px-4">
          <div className="flex items-center gap-1 overflow-x-auto">
            <NavItem icon={<TrendingUp className="w-4 h-4" />} text="TRENDING" highlight />
            <NavItem icon={<Flame className="w-4 h-4" />} text="GOTTA HAVE IT" to="/gotta-have-it" highlight special />
            <NavItem icon={<Crown className="w-4 h-4" />} text="BEST OF 2026" to="/best-of" highlight />
            <NavItem icon={<ArrowLeftRight className="w-4 h-4" />} text="COMPARE" to="/compare" />
            <NavItem icon={<Newspaper className="w-4 h-4" />} text="TECH NEWS" to="/tech-news" />
            <NavItem icon={<Swords className="w-4 h-4" />} text="VERSUS" to="/versus" />
            <NavItem icon={<MessageCircle className="w-4 h-4" />} text="FORUM" to="/forum" />
            <NavItem icon={<Trophy className="w-4 h-4" />} text="LEADERBOARD" to="/leaderboard" />
          </div>
        </div>
      </nav>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </header>
  );
};

const NavItem = ({ icon, text, highlight, special, to = "#" }) => (
  <Link
    to={to}
    className={`flex items-center gap-2 px-5 py-3 font-bold text-xs uppercase tracking-wider transition-all whitespace-nowrap ${
      special
        ? 'bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white animate-pulse shadow-lg shadow-red-500/50'
        : highlight
        ? 'bg-gradient-to-r from-[#00ff88] to-[#00d9ff] text-[#0a0a0b] neon-glow'
        : 'text-[#b4b4b9] hover:text-[#00ff88] hover:bg-[#1a1a1f]'
    }`}
    style={{fontFamily: 'Space Grotesk, sans-serif'}}
  >
    {icon}
    <span>{text}</span>
  </Link>
);

export default OhTechYesHeader;
